import React from "react";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  changePassword,
  createUserByAdmin,
  getMe,
  updateProfile,
  type AuthMe,
} from "../../lib/api";

export default function Account() {
  const [me, setMe] = React.useState<AuthMe | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [fullName, setFullName] = React.useState("");
  const [email, setEmail] = React.useState("");

  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");

  const [inviteEmail, setInviteEmail] = React.useState("");
  const [inviteRole, setInviteRole] = React.useState<"admin" | "user">("user");
  const [invitePassword, setInvitePassword] = React.useState("");
  const [savingProfile, setSavingProfile] = React.useState(false);
  const [savingPassword, setSavingPassword] = React.useState(false);
  const [savingUser, setSavingUser] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await getMe();
      setMe(data);
      setFullName(data.full_name || "");
      setEmail(data.email || "");
    } catch (err: any) {
      setError(err?.message || "Errore caricamento profilo");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const onProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const updated = await updateProfile({ full_name: fullName, email });
      setMe(updated);
      setFullName(updated.full_name || "");
      setEmail(updated.email || "");
      toast.success("Profilo aggiornato con successo");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || "Errore aggiornamento profilo");
    } finally {
      setSavingProfile(false);
    }
  };

  const onPasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      toast.success("Password aggiornata con successo");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || "Errore aggiornamento password");
    } finally {
      setSavingPassword(false);
    }
  };

  const onInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingUser(true);
    try {
      await createUserByAdmin(inviteEmail, invitePassword, inviteRole);
      setInviteEmail("");
      setInvitePassword("");
      setInviteRole("user");
      toast.success("Utente creato con successo");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || "Errore creazione utente");
    } finally {
      setSavingUser(false);
    }
  };

  if (loading) return <div className="text-sm">Caricamento profilo...</div>;
  if (error) return <div className="text-sm text-red-600">{error}</div>;

  const isAdmin = !!me?.roles?.includes("admin");

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-semibold">Account</h1>

      <form onSubmit={onProfileSave} className="rounded-lg border bg-white p-4 space-y-3">
        <h2 className="font-medium">Profilo</h2>
        <input
          className="w-full rounded border px-3 py-2"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Nome completo"
        />
        <input
          className="w-full rounded border px-3 py-2"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          type="email"
        />
        <button
          className="rounded bg-slate-900 px-4 py-2 text-white disabled:opacity-60 inline-flex items-center gap-2"
          type="submit"
          disabled={savingProfile}
        >
          {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Salva profilo
        </button>
      </form>

      <form onSubmit={onPasswordSave} className="rounded-lg border bg-white p-4 space-y-3">
        <h2 className="font-medium">Password</h2>
        <input
          className="w-full rounded border px-3 py-2"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          placeholder="Password attuale"
          type="password"
        />
        <input
          className="w-full rounded border px-3 py-2"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Nuova password"
          type="password"
          minLength={8}
        />
        <button
          className="rounded bg-slate-900 px-4 py-2 text-white disabled:opacity-60 inline-flex items-center gap-2"
          type="submit"
          disabled={savingPassword}
        >
          {savingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Aggiorna password
        </button>
      </form>

      {isAdmin && (
        <form onSubmit={onInvite} className="rounded-lg border bg-white p-4 space-y-3">
          <h2 className="font-medium">Crea utente</h2>
          <input
            className="w-full rounded border px-3 py-2"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="Email utente"
            type="email"
          />
          <input
            className="w-full rounded border px-3 py-2"
            value={invitePassword}
            onChange={(e) => setInvitePassword(e.target.value)}
            placeholder="Password iniziale"
            type="password"
            minLength={8}
          />
          <select
            className="w-full rounded border px-3 py-2"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as "admin" | "user")}
          >
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
          <button
            className="rounded bg-slate-900 px-4 py-2 text-white disabled:opacity-60 inline-flex items-center gap-2"
            type="submit"
            disabled={savingUser}
          >
            {savingUser ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Crea utente
          </button>
        </form>
      )}
    </div>
  );
}
